import { useContext, useState, useEffect } from 'react'
import Section from './_Section'
import createGradientElement from '../../helpers/markup'
import { addBgImageClass, lightOrDark, normalizeHex, transformPaddingToTailwind } from '../../helpers/style'
import ButtonEl from '../helpers/ctas/Button'
import { ArrowRightIcon, LockClosedIcon } from '@heroicons/react/24/solid'
import HeroTitle from '../helpers/CardListingHeroTitle'
import TitleBlock from '../helpers/TitleBlock'
import Picture from '../helpers/Picture'
import { MarkupContext } from '../../providers/Markup'
import MetaBadge from '../helpers/MetaBadge'
import { formatDate } from '../../helpers/util'
import { appendListingParams } from '../../helpers/listingPageUrl'
import { dataLayerPush } from '../../helpers/thirdparty'

function normalizeLayoutVariant(value) {
  const variant = (value || 'standard').toLowerCase()
  if (variant === 'first_bigger') return 'first_bigger'
  if (variant === 'featured_list') return 'featured_list'
  return 'standard'
}

/** Grid container — first_bigger: 1 col mobile, 40/30/30 tablet (10-col), 50/25/25 desktop (4-col). */
function getTagListingGridClasses(layoutVariant) {
  if (layoutVariant === 'first_bigger') {
    return 'grid grid-cols-1 md:grid-cols-10 lg:grid-cols-4 items-start'
  }
  return 'grid grid-cols-1 md:grid-cols-6 items-start'
}

/** Standard equal-thirds placement (standard / featured_list only). */
const TAG_LISTING_STANDARD_ITEM_GRID = `md:nth-[3n+3]:col-start-5
            md:nth-[3n+2]:col-start-3
            md:nth-[3n+1]:col-start-1
            md:[&:last-child:first-child]:col-start-3
            md:[&:last-child:nth-child(3n+1)]:col-start-3
            md:[&:nth-last-child(2):first-child]:col-start-2
            md:[&:nth-last-child(2):nth-child(3n+1)]:col-start-2
            md:[&:nth-child(2):last-child]:col-start-4
            md:[&:last-child:nth-child(3n+2)]:col-start-4
            col-span-2`

/**
 * Per-card column placement.
 * first_bigger: full width mobile; md 4+3+3 (~40/30/30); lg 2+1+1 (50/25/25).
 */
function getTagListingItemGridClasses(layoutVariant, index) {
  if (layoutVariant === 'first_bigger') {
    const posInRow = index % 3
    if (posInRow === 0) {
      return 'col-span-full md:col-span-4 lg:col-span-2 lg:col-start-auto'
    }
    return 'col-span-full md:col-span-3 lg:col-span-1 lg:col-start-auto'
  }

  return TAG_LISTING_STANDARD_ITEM_GRID
}

const TAG_LISTING_IMAGE_FILL =
  '[&_picture]:block [&_picture]:size-full [&_img]:size-full [&_img]:object-cover'

/** Design spec: first_bigger 288×244; standard / featured_list (equal columns) 384×224. */
function getTagListingImageWrapperClasses(layoutVariant) {
  const base = `w-full mb-5 overflow-hidden rounded-e-3xl rounded-bl-3xl ${TAG_LISTING_IMAGE_FILL}`

  if (layoutVariant === 'first_bigger') {
    return `${base} aspect-[288/244] md:h-[244px] md:aspect-auto`
  }

  return `${base} aspect-[384/224] md:h-[224px] md:aspect-auto`
}

function TagListing(props) {
  const { createMarkup } = useContext(MarkupContext)

  let cardItems = (props.items || []).filter((cardItem) => !cardItem.hideInListing)
  const componentPadding = transformPaddingToTailwind(props.componentPadding)

  // Append the listing filter params to each editor-provided CTA link URL (top-right "Search"
  // button et al.) so they carry content-type/topic/trainingFormat through to the search page.
  const ctaLinkItems = (props.ctaLinkItem || [])
    .filter((item) => item.link?.url)
    .map((item) => ({
      ...item,
      link: {
        ...item.link,
        url: appendListingParams(
          item.link.url,
          props.filteredContentType,
          props.filteredByTopic,
          props.filteredByTrainingFormat,
        ),
      },
    }))

  // "View more" reuses the first CTA's (param-appended) URL; falls back to in-place expansion
  // when no CTA link is configured.
  const listingPageUrl = ctaLinkItems[0]?.link?.url || null

  const initialNumber = props.initialNoOfCards || 3
  const [viewMore, setViewMore] = useState(cardItems.length <= initialNumber)
  const [alignment, setAlignment] = useState('center')
  const [textAlignment, setTextAlignment] = useState({
    flex: 'center',
    align: 'center',
  })
  // Normalise the editor's cardVariant to a canonical token (mirrors CardListDynamic). The DTO
  // sends capitalised values ('News', 'Hero image', 'Background image', 'Icon'); comparing those
  // against lowercase 'news' silently failed, so the News variant rendered as a background card.
  const rawVariant = props.cardVariant
  const variant =
    !rawVariant || rawVariant === 'Icon'
      ? 'icon'
      : rawVariant === 'Hero image'
        ? 'hero'
        : rawVariant === 'News'
          ? 'news'
          : 'background'
  const layoutVariant = normalizeLayoutVariant(props.layoutVariant)
  const [backgroundColor, setBackgroundColor] = useState('transparent')
  const [gradientColor, setGradientColor] = useState('#fff')
  const cardLightOrDark = lightOrDark(backgroundColor)
  const hasCardBackground = backgroundColor !== 'transparent'

  useEffect(() => {
    setTextAlignment((prevState) => {
      const newState = { ...prevState }
      const alignment = props.contentAlignment?.toLowerCase() ?? 'center'
      if (newState.align !== props.contentAlignment?.toLowerCase()) {
        newState.flex = alignment === 'center' ? 'center' : 'center'
        newState.align = alignment === 'center' ? 'center' : 'left'
      }
      setAlignment(alignment)
      return newState
    })
    const bgColor = normalizeHex(props.cardBackgroundColour)
    const fgColor = normalizeHex(props.imageGradientColour)
    setBackgroundColor((prevState) => bgColor || prevState)
    setGradientColor((prevState) => fgColor || prevState)
  }, [])

  if (!viewMore) {
    cardItems = cardItems.slice(0, initialNumber)
  }

  return (
    <Section
      type="tagListing"
      sectionTitle={false}
      innerClass={`px-6! lg:px-8! text-${textAlignment.align} flex ${componentPadding}`}
      {...props}
    >
      <div className="w-full">
        <div className="flex flex-row items-center justify-between gap-6 md:gap-8 mb-8">
          {/* Title Block Container */}
          {(props.title || props.description) && (
            <div className="flex-1 [&_*]:!my-0">
              <TitleBlock {...props} headingClass={'text-(--color-primary-text) !font-light'} />
            </div>
          )}

          {/* Buttons Container */}
          {ctaLinkItems.length > 0 && (
            <div className="flex flex-wrap gap-4 shrink-0">
              {ctaLinkItems.map((ctaLinkItem, i) => (
                <ButtonEl
                  key={i}
                  link={ctaLinkItem.link}
                  label={
                    <>
                      <span className="md:hidden">View all</span>
                      <span className="hidden md:inline">{ctaLinkItem.link?.name}</span>
                    </>
                  }
                  icon={ctaLinkItem.linkIconClass}
                  position={ctaLinkItem.linkPosition}
                  theme={ctaLinkItem.linkType.toLowerCase()}
                  className={`py-0! ${!props.backgroundColor && ctaLinkItem.linkType.toLowerCase() === 'text' ? 'text-(--color-primary-text) font-normal! text-lg! underline   hover:no-underline' : ''}`}
                />
              ))}
            </div>
          )}
        </div>
        <hr className="w-full mt-8 border-t border-(--color-border-tertiary)" />

        {cardItems.length != 0 && (
          <div
            className={`${getTagListingGridClasses(layoutVariant)} ${props.title?.length || props.description?.length ? 'pt-10' : 'pt-0'} gap-8`}
          >
            {cardItems.map((cardItem, i) => {
              const tags = (cardItem.tagsConfig && JSON.parse(cardItem.tagsConfig).tags) || []
              const metaBadgeData = cardItem.metaBadgeData || {}
              const badgePrimaryText = metaBadgeData.categoryTags?.[0] || cardItem?.topics?.[0]
              const badgeSecondaryText = metaBadgeData.readTime ? `${metaBadgeData.readTime}` : ''
              const badgeIsLocked = metaBadgeData.isLocked ?? false
              const itemGridClasses = getTagListingItemGridClasses(layoutVariant, i)
              const imageWrapperClasses = getTagListingImageWrapperClasses(layoutVariant)

              const handleCardClick = (e) => {
                dataLayerPush({
                  event: badgeIsLocked ? 'login_intent' : 'article_click',
                  click_text: cardItem.listingTitle,
                  category: metaBadgeData.categoryTags?.length ? metaBadgeData.categoryTags.join(', ') : undefined,
                  member_only: badgeIsLocked,
                }, e.currentTarget)
              }

              const innerHero = (
                <>
                  <div key={i} data-type="hero">
                    <div className={`relative flex flex-col`}>
                      {variant !== 'news' && (
                        <div className={imageWrapperClasses}>
                          <Picture
                            desktopImage={cardItem.listingDesktopImage}
                            mobileImage={cardItem.listingMobileImage}
                          />
                        </div>
                      )}
                      <div className="flex flex-row justify-between items-center w-full mb-4">
                        <MetaBadge
                          primaryText={badgePrimaryText}
                          secondaryText={badgeSecondaryText}
                          isLocked={badgeIsLocked}
                          icon={badgeIsLocked ? <LockClosedIcon className="w-3.5 h-3.5" aria-hidden="true" /> : null}
                          tagType={badgeIsLocked ? 'locked_news_articles' : 'unlocked_news_articles'}
                        />
                        {cardItem?.dateTimeFormatted && (
                          <span className="text-xs font-normal text-(--sb-common-base-body-text-color) shrink-0 ml-4">
                            {formatDate(cardItem.dateTimeFormatted)}
                          </span>
                        )}
                      </div>

                      <div className="col-start-2 col-span-7 md:col-span-3 lg:col-span-5">
                        {cardItem.listingTitle && (
                          <HeroTitle
                            type="dynamic"
                            cardItem={cardItem}
                            idx={i}
                            className={'text-(--color-primary-text) text-3xl md:text-4xl font-light'}
                            onCardClick={handleCardClick}
                          />
                        )}
                        {cardItem.listingDescription && (
                          <div
                            className="mt-mt-4 !text-(--sb-common-base-body-text-color)"
                            data-rte="true"
                            dangerouslySetInnerHTML={createMarkup(cardItem.listingDescription)}
                          />
                        )}
                      </div>
                      <div className="flex gap-6 mt-4">
                        <ButtonEl
                          key={i}
                          link={{ name: cardItem.dynamicCardLinkText, target: '_blank', url: cardItem.listingLink }}
                          theme={'text'}
                          className={`!text-sm !text-(--color-primary-text) !font-normal`}
                          onClick={handleCardClick}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )

              const innerBG = (
                <>
                  {props.imageGradientColour &&
                    createGradientElement({
                      type: props.imageGradientType,
                      fromColor: gradientColor,
                    })}
                  <div data-type="bg" className="relative min-h-96 h-full mt-auto flex flex-col justify-end">
                    <div className="flex flex-row justify-between items-center w-full mb-4">
                      <MetaBadge
                        primaryText={badgePrimaryText}
                        secondaryText={badgeSecondaryText}
                        isLocked={badgeIsLocked}
                      />
                      {cardItem?.dateTimeFormatted && (
                        <span className="text-xs font-normal shrink-0 ml-4">
                          {formatDate(cardItem.dateTimeFormatted)}
                        </span>
                      )}
                    </div>
                    <div className="col-start-2 col-span-7 md:col-span-3 lg:col-span-5">
                      {cardItem.listingTitle && (
                        <HeroTitle
                          type="dynamic"
                          cardItem={cardItem}
                          idx={i}
                          className={'text-3xl md:text-4xl font-light'}
                        />
                      )}
                      {cardItem.listingDescription && (
                        <div
                          className="mt-mt-4"
                          data-rte="true"
                          dangerouslySetInnerHTML={createMarkup(cardItem.listingDescription)}
                        />
                      )}
                    </div>
                    {tags.length != 0 && <small>{tags.join(', ')}</small>}
                    {cardItem.dynamicCardLinkText && (
                      <div className={`mt-2 flex justify-${alignment !== 'center' ? 'start' : 'center'}`}>
                        <ButtonEl>{cardItem.dynamicCardLinkText}</ButtonEl>
                      </div>
                    )}
                  </div>
                </>
              )
              const hasCardBgImage = !!(cardItem.listingDesktopImage || cardItem.listingMobileImage)
              const bgProps = {
                style: {
                  '--mobile-bg-url': cardItem.listingMobileImage ? `url(${cardItem.listingMobileImage.src})` : null,
                  '--desktop-bg-url': cardItem.listingDesktopImage ? `url(${cardItem.listingDesktopImage.src})` : null,
                  '--bg-color': backgroundColor,
                  '--light-or-dark': cardLightOrDark,
                  // Only claim --text-color when the card paints its own background. A
                  // transparent card shows the section background through, so it has to
                  // inherit the section's --text-color — otherwise a dark section gets
                  // dark text.
                  ...(hasCardBackground || hasCardBgImage
                    ? {
                        '--text-color':
                          cardLightOrDark === 'dark' || hasCardBgImage
                            ? 'var(--color-light)'
                            : 'var(--color-dark)',
                      }
                    : {}),
                },
                className: `${itemGridClasses} 
                            rounded-e-3xl rounded-bl-3xl
                            relative w-full h-full bg-(--bg-color) pt-4 px-8 pb-8 overflow-hidden text-(--text-color) ${addBgImageClass(cardItem.listingDesktopImage?.src, cardItem.listingMobileImage?.src)}`,
              }

              const inner = variant !== 'hero' && variant !== 'news' ? innerBG : innerHero
              if (variant !== 'hero' && variant !== 'news') {
                return cardItem.listingLink ? (
                  <a href={cardItem.listingLink} key={i} {...bgProps} onClick={handleCardClick}>
                    {inner}
                  </a>
                ) : (
                  <div key={i} {...bgProps}>
                    {inner}
                  </div>
                )
              } else {
                return (
                  <div
                    key={i}
                    className={`${itemGridClasses} relative w-full pt-4 pb-8 rounded-2xl overflow-hidden`}
                  >
                    {inner}
                  </div>
                )
              }
            })}
          </div>
        )}
        {!viewMore && (
          <div className="flex justify-center mt-20">
            <ButtonEl
              {...(listingPageUrl
                ? { link: { url: listingPageUrl } }
                : {
                    onClick: () => {
                      setViewMore(true)
                    },
                  })}
            >
              {props.viewMoreText || 'View more'}
              <ArrowRightIcon role="none" className="h-6 w-6 ml-2" />
            </ButtonEl>
          </div>
        )}
      </div>
    </Section>
  )
}

export default TagListing
